import type {
  HardwareConfigEquipmentMapping,
  HardwareConfigSchema,
} from './types';

export const hardwareConfigEquipmentMappingToEquipmentKeyToEquipmentId = (
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping,
): Record<string, string> => {
  return Object.values(hardwareConfigEquipmentMapping).reduce(
    (acc, robotMap) => {
      const robotEquipmentMap = Object.entries(
        robotMap.hardwareKeyToEquipmentId,
      ).reduce(
        (innerAcc, [hardwareKey, equipmentId]) => {
          return {
            ...innerAcc,
            [hardwareKey]: equipmentId,
          };
        },
        {} as Record<string, string>,
      );

      return {
        ...acc,
        ...robotEquipmentMap,
      };
    },
    {} as Record<string, string>,
  );
};

/**
 * Extract hardware keys from hardware configs
 * @param hardwareConfigs Single hardware config or array of hardware configurations
 * @returns Array of unique hardware keys across all configs
 */
export const extractHardwareKeysFromConfig = (
  hardwareConfigs: HardwareConfigSchema | HardwareConfigSchema[],
): string[] => {
  const configs = Array.isArray(hardwareConfigs)
    ? hardwareConfigs
    : [hardwareConfigs];

  return [
    ...new Set(
      configs.flatMap((config) =>
        Object.keys(config.robots).flatMap((robotId) =>
          Object.keys(config.robots[robotId].hardware),
        ),
      ),
    ),
  ];
};

/**
 * Validate that all hardware configs contain the required hardware keys
 * @param requiredKeys Hardware keys that must be present in all configs
 * @param hardwareConfigKeys Array of hardware key arrays to validate
 * @returns Object with validation result and missing keys
 */
export const validateAllHardwareConfigsContainKeys = (
  requiredKeys: string[],
  hardwareConfigKeys: string[][],
): {
  isValid: boolean;
  missingHardwareKeys: string[];
  error?: string;
} => {
  if (requiredKeys.length === 0 || hardwareConfigKeys.length === 0) {
    return {
      isValid: true,
      missingHardwareKeys: [],
    };
  }

  const missingHardwareConfigKeys = requiredKeys.filter(
    (key) => !hardwareConfigKeys.every((keys) => keys.includes(key)),
  );

  if (missingHardwareConfigKeys.length > 0) {
    return {
      isValid: false,
      missingHardwareKeys: missingHardwareConfigKeys,
      error: `Hardware config keys ${missingHardwareConfigKeys.join(', ')} not found in all provided configs`,
    };
  }

  return {
    isValid: true,
    missingHardwareKeys: [],
  };
};

// Validates input modelArchitectures are compatible with each other
export const validateModelArchitecturesCompatibility = (
  modelArchitectures: Array<{
    id: string;
    name: string;
    inputHardwareConfig?: { config: any } | null;
    outputHardwareConfig?: { config: any } | null;
  }>,
): {
  isValid: boolean;
  message?: string;
} => {
  if (modelArchitectures.length === 0) {
    return { isValid: true };
  }

  const validArchitectures = modelArchitectures.filter(
    (arch) =>
      arch.inputHardwareConfig?.config && arch.outputHardwareConfig?.config,
  );

  if (validArchitectures.length === 0) {
    return { isValid: true };
  }

  const allInputKeys = extractHardwareKeysFromConfig(
    validArchitectures.map((arch) => arch.inputHardwareConfig!.config),
  );

  const allOutputKeys = extractHardwareKeysFromConfig(
    validArchitectures.map((arch) => arch.outputHardwareConfig!.config),
  );

  const inputKeysPerArch = validArchitectures.map((arch) =>
    extractHardwareKeysFromConfig(arch.inputHardwareConfig!.config),
  );

  const outputKeysPerArch = validArchitectures.map((arch) =>
    extractHardwareKeysFromConfig(arch.outputHardwareConfig!.config),
  );

  const inputValidation = validateAllHardwareConfigsContainKeys(
    allInputKeys,
    inputKeysPerArch,
  );

  const outputValidation = validateAllHardwareConfigsContainKeys(
    allOutputKeys,
    outputKeysPerArch,
  );

  const isValid = inputValidation.isValid && outputValidation.isValid;

  return {
    isValid,
    message: isValid
      ? undefined
      : 'Selected models have incompatible hardware configurations.',
  };
};

/**
 * Filter tasks to show only those compatible with the selected model architectures
 * @param modelArchitectures Model architectures from selected training runs
 * @param allTasks All available tasks from GraphQL
 * @param modelTaskIds Task IDs that were used to train the selected models
 * @returns Filtered tasks and default selected tasks
 */
export const filterTasksForTrainingRuns = (
  modelArchitectures: Array<{
    id: string;
    name: string;
    inputHardwareConfig?: { config: any } | null;
    outputHardwareConfig?: { config: any } | null;
  }>,
  allTasks: Array<{
    id: string;
    name: string;
    hardwareConfig?: { config: any } | null;
  }>,
  modelTaskIds: string[],
): {
  transformedTasks: Array<{
    id: string;
    name: string;
    hardwareConfig?: HardwareConfigSchema | null;
  }>;
  filteredTasks: Array<{
    id: string;
    name: string;
    hardwareConfig?: HardwareConfigSchema | null;
  }>;
  defaultSelectedTaskIds: string[];
} => {
  // Filter tasks to show tasks that have AT LEAST the sensors the models need
  // Tasks with null hardware configs are always shown
  const filteredTasks = (() => {
    const allModelConfigs = modelArchitectures
      .flatMap((arch) => [
        arch.inputHardwareConfig?.config,
        arch.outputHardwareConfig?.config,
      ])
      .filter((config) => config != null);

    // If no hardware requirements, return all tasks
    if (allModelConfigs.length === 0) {
      return allTasks.map((task) => ({
        id: task.id,
        name: task.name,
        hardwareConfig: task.hardwareConfig?.config || null,
      }));
    }

    const modelArchitectureHardwareConfigKeys =
      extractHardwareKeysFromConfig(allModelConfigs);

    // Returned filtered tasks as flat map
    return allTasks
      .map((task) => ({
        id: task.id,
        name: task.name,
        hardwareConfig: task.hardwareConfig?.config || null,
      }))
      .filter((task) => {
        if (!task.hardwareConfig) {
          return true;
        }

        const validation = validateAllHardwareConfigsContainKeys(
          modelArchitectureHardwareConfigKeys,
          [extractHardwareKeysFromConfig(task.hardwareConfig)],
        );

        return validation.isValid;
      });
  })();

  // Set default selected tasks (model training tasks that pass the filter)
  const defaultSelectedTaskIds = modelTaskIds.filter((taskId) =>
    filteredTasks.some((task) => task.id === taskId),
  );

  return {
    transformedTasks: allTasks.map((task) => ({
      id: task.id,
      name: task.name,
      hardwareConfig: task.hardwareConfig?.config || null,
    })),
    filteredTasks,
    defaultSelectedTaskIds,
  };
};
